import { API_URL_P } from "./components/Constants";

const API_URL = API_URL_P + "index.php";

// Função utilitária para timeout (evita travar em requisições lentas)
async function fetchWithTimeout(resource, options = {}, timeout = 10000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(resource, { ...options, signal: controller.signal });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw new Error("Falha de conexão com o servidor.");
  }
}

/* ============================================================
   USERS
============================================================ */
export async function getUsers(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Erro ao carregar usuários.");
  return res.json();
}

export async function getUser(id, token) {
  const res = await fetch(`${API_URL}?r=users&id=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao carregar usuário");
  return data[0];
}

export async function editUser(id, nome, email, senha, token) {
  const body = { nome, email };
  if (senha) body.nova_senha = senha;
  const res = await fetch(`${API_URL}?r=users&id=${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar usuário");
  return data;
}

export async function delUser(token, userId) {
  const res = await fetch(`${API_URL}?r=users&id=${userId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const text = await res.text();
  let data = {};
  if (text.trim() !== "") {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Resposta inválida do servidor: " + text);
    }
  }
  if (!res.ok) throw new Error(data.error || "Erro ao excluir usuário");
  return data;
}

/* ============================================================
   AUTH
============================================================ */
export async function login(login, senha) {
  try {
    const res = await fetchWithTimeout(`${API_URL}?r=auth_login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ login, senha }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "Credenciais inválidas");
    }

    return data;
  } catch (err) {
    throw new Error(err.message || "Erro na comunicação com o servidor");
  }
}

export async function register(nome, email, senha) {
  const res = await fetchWithTimeout(`${API_URL}?r=auth_register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nome, email, senha }),
  });
  if (!res.ok) throw new Error("Erro ao registrar usuário.");
  return res.json();
}

export async function refreshToken(refresh_token) {
  const res = await fetchWithTimeout(`${API_URL}?r=auth_refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token }),
  });
  if (!res.ok) throw new Error("Erro ao renovar token.");
  return res.json();
}

export async function logout(refresh_token) {
  const res = await fetchWithTimeout(`${API_URL}?r=auth_logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token }),
  });
  if (!res.ok) throw new Error("Erro ao fazer logout.");
  return res.json();
}

export async function changePassword(token, senha_atual, nova_senha) {
  const res = await fetch(`${API_URL}?r=auth_changepass`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ senha_atual, nova_senha }),
  });
  const text = await res.text();
  let data = {};
  if (text.trim() !== "") {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Resposta inválida do servidor: " + text);
    }
  }
  if (!res.ok) throw new Error(data.error || "Erro ao alterar senha");
  return data;
}

/* ============================================================
   FAMILIES
============================================================ */
export async function getFamilies(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=family`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Erro ao carregar famílias");
  return res.json();
}

export async function getFamilyById(id, token) {
  const res = await fetchWithTimeout(`${API_URL}?r=family&id=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao carregar família");
  return data[0];
}

export async function createFamily(token, familia) {
  const res = await fetchWithTimeout(`${API_URL}?r=family`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ familia }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao criar família");
  return data;
}

export async function updateFamily(id, token, familia) {
  const res = await fetchWithTimeout(`${API_URL}?r=family&id=${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ familia }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar família");
  return data;
}

export async function deleteFamily(id, token) {
  const res = await fetchWithTimeout(`${API_URL}?r=family&id=${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const text = await res.text();
  let data = {};
  if (text.trim() !== "") {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Resposta inválida do servidor: " + text);
    }
  }
  if (!res.ok) throw new Error(data.error || "Erro ao deletar família");
  return data;
}

/* ============================================================
   PRODUCTS
============================================================ */
// 🧩 Buscar todos os produtos
export async function getProducts(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=product`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Erro ao carregar produtos");
  return res.json();
}

// 🧩 Buscar produto por ID
export async function getProductById(id, token) {
  const res = await fetchWithTimeout(`${API_URL}?r=product&id=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao carregar produto");
  return data[0];
}

// 🧩 Criar novo produto
export async function createProduct(token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=product`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao criar produto");
  return data;
}

// 🧩 Atualizar produto existente
export async function updateProduct(id, token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=product&id=${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar produto");
  return data;
}

// 🧩 Deletar produto
export async function deleteProduct(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=product&id=${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  const text = await res.text();
  let data = {};

  if (text.trim() !== "") {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Resposta inválida do servidor: " + text);
    }
  }

  if (!res.ok) throw new Error(data.error || "Erro ao deletar produto");
  return data;
}

/* ============================================================
   TABLES
============================================================ */

// 🔍 Buscar todas as tabelas
export async function getTables(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=tables`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) throw new Error("Erro ao carregar tabelas");
  return res.json();
}

// 🔍 Buscar tabela por ID
export async function getTableById(id, token) {
  const res = await fetchWithTimeout(`${API_URL}?r=tables&id=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao carregar tabela");
  return data[0];
}

// ➕ Criar tabela
export async function createTable(token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=tables`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao criar tabela");
  return data;
}

// ✏️ Atualizar tabela
export async function updateTable(id, token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=tables&id=${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar tabela");
  return data;
}

// ❌ Deletar tabela
export async function deleteTable(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=tables&id=${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao deletar tabela");
  return data;
}

/* ============================================================
   TABLE ITEMS
============================================================ */

// 🔍 Listar produtos de uma tabela
export async function getTableItems(token, tabelaId) {
  const res = await fetchWithTimeout(
    `${API_URL}?r=table-items&tabela_id=${tabelaId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!res.ok) throw new Error("Erro ao carregar itens da tabela");
  return res.json();
}

// ➕ Adicionar produto na tabela
export async function addProductToTable(token, data) {
  const res = await fetchWithTimeout(`${API_URL}?r=table-items`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Erro ao adicionar produto");
  return json;
}

// ✏️ Atualizar preço
export async function updateTableItem(id, token, valor) {
  const res = await fetchWithTimeout(`${API_URL}?r=table-items&id=${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ valor }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar valor");
  return data;
}

// ❌ Remover item
export async function deleteTableItem(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=table-items&id=${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao remover item");
  return data;
}

/* ============================================================
   TIPOS
============================================================ */
// 🧩 Buscar todos os tipos
export async function getTipos(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=tipos`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) throw new Error("Erro ao carregar tipos");
  return res.json();
}

// 🧩 Buscar tipo por ID
export async function getTipoById(id, token) {
  const res = await fetchWithTimeout(`${API_URL}?r=tipos&id=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao carregar tipo");

  return data[0];
}

// 🧩 Criar novo tipo
export async function createTipo(token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=tipos`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao criar tipo");

  return data;
}

// 🧩 Atualizar tipo
export async function updateTipo(id, token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=tipos&id=${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar tipo");

  return data;
}

// 🧩 Deletar tipo
export async function deleteTipo(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=tipos&id=${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  const text = await res.text();
  let data = {};

  if (text.trim() !== "") {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Resposta inválida do servidor: " + text);
    }
  }

  if (!res.ok) throw new Error(data.error || "Erro ao deletar tipo");

  return data;
}

/* ============================================================
   EVENTOS
============================================================ */
// 🧩 Buscar todos os eventos
export async function getEventos(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=eventos`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) throw new Error("Erro ao carregar eventos");
  return res.json();
}

// 🧩 Buscar evento por ID
export async function getEventoById(id, token) {
  const res = await fetchWithTimeout(`${API_URL}?r=eventos&id=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao carregar evento");

  return data[0];
}

// 🧩 Criar novo evento
export async function createEvento(token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=eventos`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao criar evento");

  return data;
}

// 🧩 Atualizar tieventopo
export async function updateEvento(id, token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=eventos&id=${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar evento");

  return data;
}

// 🧩 Deletar evento
export async function deleteEvento(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=eventos&id=${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  const text = await res.text();
  let data = {};

  if (text.trim() !== "") {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Resposta inválida do servidor: " + text);
    }
  }

  if (!res.ok) throw new Error(data.error || "Erro ao deletar evento");

  return data;
}

/* ============================================================
   CLIENTS
============================================================ */
// 🧩 Buscar todos os clientes
export async function getClients(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=clients`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Erro ao carregar clientes");
  return res.json();
}

// 🧩 Buscar cliente por ID
export async function getClientById(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=clients&id=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao carregar cliente");
  return data[0];
}

// 🧩 Criar novo cliente
export async function createClient(token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=clients`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  //if (!res.ok) throw new Error(data.error || "Erro ao criar cliente");
  if (!res.ok) {
    const error = new Error(data.error || "Erro ao criar cliente");
    error.status = res.status;
    error.code = data.code;
    error.cliente_id = data.cliente_id;
    error.razao_social = data.razao_social;
    throw error;
  }
  return data;
}

// 🧩 Atualizar cliente existente
export async function updateClient(token, id, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=clients&id=${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar cliente");
  return data;
}

// 🧩 Deletar cliente
export async function deleteClient(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=clients&id=${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  const text = await res.text();
  let data = {};

  if (text.trim() !== "") {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Resposta inválida do servidor: " + text);
    }
  }

  if (!res.ok) throw new Error(data.error || "Erro ao deletar cliente");
  return data;
}


/* ============================================================
   COTAÇÕES
============================================================ */
export async function getCotacoes(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=cotacoes`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Erro ao carregar cotações");
  const data = await res.json();
  // data deve ser array; se backend retornar objeto, normalize:
  return Array.isArray(data) ? data : [data];
}

export async function getCotacaoById(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=cotacoes&id=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text();
    let err = text || "Erro ao carregar cotação";
    throw new Error(err);
  }
  const data = await res.json();
  // Se backend retornar array com 1 elemento, transforme em objeto
  if (Array.isArray(data) && data.length > 0) return data[0];
  return data;
}

export async function createCotacao(token, cotacaoData) {
  const res = await fetchWithTimeout(`${API_URL}?r=cotacoes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(cotacaoData),
  });
  if (!res.ok) throw new Error("Erro ao criar cotação");
  return res.json();
}

export async function updateCotacao(token, data) {
  const res = await fetchWithTimeout(`${API_URL}?r=cotacoes`, {
    method: "PUT",
    headers: { 
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error("Erro ao atualizar");
  return res.json();
}

export async function deleteCotacao(token, id) {
  const res = await fetchWithTimeout(`${API_URL}?r=cotacoes&id=${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  const text = await res.text();
  let data = {};

  if (text.trim() !== "") {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Resposta inválida do servidor: " + text);
    }
  }

  if (!res.ok) throw new Error(data.error || "Erro ao deletar cotação");
  return data;
}


/* ============================================================
   CONFIGURAÇÕES
============================================================ */
export async function getConfigs(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=configuracoes`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) throw new Error("Erro ao carregar configurações");
  return res.json();
}

export async function updateConfigs(token, form) {
  const res = await fetchWithTimeout(`${API_URL}?r=configuracoes`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(form),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Erro ao atualizar configurações");
  return data;
}

export async function getAppConfigs(token) {
  const res = await fetch(`${API_URL}?r=ajustes`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return res.json();
}

export async function updateAppConfigs(token, data) {
  const res = await fetch(`${API_URL}?r=ajustes`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });
  return res.json();
}

/* ============================================================
   TELEGRAM
============================================================ */
export async function gerarTokenTelegram(token) {
  const res = await fetchWithTimeout(`${API_URL}?r=gerar_token_telegram`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await res.json();

  if (!res.ok) throw new Error(data.error || "Erro ao gerar token Telegram");

  return data;
}
